import type { ReactNode } from 'react';
import { useGT } from 'gt-react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { buyingOrderReadiness, formatCurrency } from '@erp/shared-utils';
import type { BuyingOrderRow } from '@erp/rpc';
import type { ColumnConfigEntry } from '@erp/shared-types';
import {
  type ColumnDef,
  type VisibilityState,
  appTableFeatures,
  useTable,
} from '@/lib/tanstack-table';
import { DataTable } from '@/components/common/data-table/data-table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  BUYING_ORDERS_PAGE_SIZE,
  buyingRowKey,
  type BuyingOrderSort,
  type SortDirection,
} from '../utils/buyingOrdersUrl';
import { buyerCodesLabel } from '../utils/buyerCodesLabel';
import { lineTypeTooltip } from '../utils/lineTypeTooltip';
import { useThresholdBufferPct } from '../utils/useThresholdBufferPct';
import { oldestSoLabel } from '../utils/oldestSoLabel';
import { OrderReadinessButton } from './OrderReadiness';
import { ColumnStyledCell } from './ColumnStyledCell';
import type { BuyerBoardColumnConfig } from './useBuyerBoardColumnConfig';
import { useBuyerBoardTableState } from './useBuyerBoardTableState';
import { SELECTED_ROW_CLASS } from './selectedRowClass';

interface BuyingOrdersTableProps {
  rows: BuyingOrderRow[];
  totalRows: number;
  /** True only before the first response; refetches keep the previous rows. */
  isLoading: boolean;
  sort: BuyingOrderSort;
  direction: SortDirection;
  onSortChange: (sort: BuyingOrderSort) => void;
  isSelected: (row: BuyingOrderRow) => boolean;
  /** Keyboard cursor (j/k): the row Enter would open. */
  cursorKey?: string | null;
  onSelect: (row: BuyingOrderRow) => void;
  /** 1-based. */
  page: number;
  onPageChange: (page: number) => void;
  /** `false` hides a column (one a filter narrowed to one value). */
  columnVisibility?: VisibilityState;
  /** The saved Orders view: column choice, order, titles, alignment, widths. */
  columnConfig: BuyerBoardColumnConfig;
}

const ALL_COLUMNS_VISIBLE: VisibilityState = {};
const SKELETON_ROW_COUNT = 8;
const SKELETON_KEYS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => `skeleton-${i}`,
);

function Count({ value }: { value: number }) {
  return (
    <span className={cn(value === 0 && 'text-muted-foreground')}>
      {value.toLocaleString()}
    </span>
  );
}

/** Committed dollars of one line type; the tooltip carries the counts. */
function TypeValue({ value, title }: { value: number | null; title?: string }) {
  if (value === null) return null;
  return (
    <span
      title={title}
      className={cn('block', value === 0 && 'text-muted-foreground')}
    >
      {formatCurrency(value)}
    </span>
  );
}

/**
 * A vendor minimum as the feed quotes it for this ship-to (GEN-3151): blank
 * when the vendor quotes none, quotes a "never" placeholder, or the row spans
 * several carts. A $1 minimum reads as "no hard minimum" to Rexel's buyers, so
 * it shows as is. Never the readiness fallback: that is a check, not a quote.
 */
function VendorMinimum({ value }: { value: number | null }) {
  if (value === null) return null;
  return <span className="block">{formatCurrency(value)}</span>;
}

/** "12 core": how many of the order's items sit in a core-offer program. */
function CoreCount({
  value,
  gt,
}: {
  value: number | null;
  gt: ReturnType<typeof useGT>;
}) {
  if (value === null) return null;
  return (
    <span className={cn('block', value === 0 && 'text-muted-foreground')}>
      {gt('{count} core', {
        context: 'buying orders core cell, count of core-offer items',
        count: value.toLocaleString(),
      })}
    </span>
  );
}

function ReadinessCell({ row }: { row: BuyingOrderRow }) {
  const readiness = buyingOrderReadiness(row, useThresholdBufferPct());
  if (readiness === null) {
    return <span className="block text-center text-muted-foreground">—</span>;
  }
  return (
    <span className="flex justify-center">
      <OrderReadinessButton readiness={readiness} />
    </span>
  );
}

function SortHeader({
  label,
  sortKey,
  sort,
  direction,
  onSortChange,
  align = 'left',
}: {
  label: string;
  sortKey: BuyingOrderSort;
  sort: BuyingOrderSort;
  direction: SortDirection;
  onSortChange: (sort: BuyingOrderSort) => void;
  align?: 'left' | 'right';
}) {
  const active = sort === sortKey;
  const Arrow = direction === 'asc' ? ArrowUpIcon : ArrowDownIcon;
  return (
    <button
      type="button"
      onClick={() => onSortChange(sortKey)}
      className={cn(
        'inline-flex items-center gap-0.5 font-medium hover:text-foreground',
        active && 'text-foreground',
        align === 'right' && 'w-full flex-row-reverse justify-start',
      )}
    >
      {label}
      {active && <Arrow aria-hidden className="h-3 w-3" />}
    </button>
  );
}

/** One line that clips with an ellipsis, the full text in the tooltip. */
function TruncatedText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  return (
    <span
      title={text}
      className={cn(
        'block w-0 min-w-full truncate whitespace-nowrap',
        className,
      )}
    >
      {text}
    </span>
  );
}

function VendorCell({ row }: { row: BuyingOrderRow }) {
  if (row.kind === 'to') {
    const source = row.sourceWarehouseCode ?? row.sourceWarehouseName ?? '';
    const dest = row.shipToWarehouseCode ?? row.shipToWarehouseName ?? '';
    return (
      <TruncatedText
        text={`${source} → ${dest}`}
        className="font-mono text-[11px]"
      />
    );
  }
  const vendor = row.supplierName ?? '';
  const shipTo = row.shipToWarehouseName;
  // The open order's row names where it ships, so the detail header below
  // need not repeat it (GEN-3230). Other rows keep the vendor alone.
  return (
    <span
      title={shipTo ? `${vendor} → ${shipTo}` : vendor}
      className="block w-0 min-w-full truncate whitespace-nowrap"
    >
      <span className="font-medium">{vendor}</span>
      {shipTo && (
        <span className="hidden text-muted-foreground [[aria-selected=true]_&]:inline">
          {' → '}
          {shipTo}
        </span>
      )}
    </span>
  );
}

/** One Orders column: what it draws; its title and alignment come from the saved view. */
interface OrderColumnSpec {
  id: string;
  size: number;
  /** Set when a header click sorts the Orders list on the server. */
  sortKey?: BuyingOrderSort;
  centerHeader?: boolean;
  cell: (row: BuyingOrderRow) => ReactNode;
}

interface SortHeaderState {
  sort: BuyingOrderSort;
  direction: SortDirection;
  onSortChange: (sort: BuyingOrderSort) => void;
}

/** What each Orders column draws, keyed by id; titles come from the saved view. */
function orderColumnSpecs(gt: ReturnType<typeof useGT>): OrderColumnSpec[] {
  const tooltipLabels = {
    lines: (count: number) =>
      count === 1
        ? gt('1 line', { context: 'buying order summary line count' })
        : gt('{count} lines', {
            context: 'buying order summary line count',
            count: String(count),
          }),
    committed: (count: number) =>
      gt('{count} committed', {
        context: 'buying orders dollars tooltip, committed line count',
        count: String(count),
      }),
    shareable: (count: number) =>
      count === 1
        ? gt('1 line a transfer could cover', {
            context: 'buying orders sharing dollars tooltip',
          })
        : gt('{count} lines a transfer could cover', {
            context: 'buying orders sharing dollars tooltip',
            count: String(count),
          }),
  };
  const typeColumn = (spec: {
    id: string;
    sortKey: BuyingOrderSort;
    value: (row: BuyingOrderRow) => number;
    count: (row: BuyingOrderRow) => number;
    committed: (row: BuyingOrderRow) => number;
  }): OrderColumnSpec => ({
    id: spec.id,
    size: 84,
    sortKey: spec.sortKey,
    cell: (row) => (
      <TypeValue
        value={spec.value(row)}
        title={lineTypeTooltip(
          spec.count(row),
          spec.committed(row),
          tooltipLabels,
        )}
      />
    ),
  });
  return [
    {
      id: 'vendor',
      size: 1000,
      sortKey: 'name',
      cell: (row) => <VendorCell row={row} />,
    },
    {
      id: 'shipTo',
      size: 72,
      cell: (row) =>
        row.shipToWarehouseId === null ? (
          <span className="whitespace-nowrap text-muted-foreground">
            {row.locationCount === 1
              ? gt('1 location', {
                  context: 'buying orders ship-to cell, vendor grouping',
                })
              : gt('{count} locations', {
                  context: 'buying orders ship-to cell, vendor grouping',
                  count: String(row.locationCount),
                })}
          </span>
        ) : (
          <span className="font-mono text-[11px]">
            {row.shipToWarehouseCode ?? row.shipToWarehouseName}
          </span>
        ),
    },
    {
      id: 'buyer',
      size: 64,
      cell: (row) => {
        const label = buyerCodesLabel(row.buyerCodes);
        return label === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <TruncatedText text={label} className="font-mono text-[11px]" />
        );
      },
    },
    typeColumn({
      id: 'backOrders',
      sortKey: 'back_order_value',
      value: (row) => row.backOrderValue,
      count: (row) => row.backOrderCount,
      committed: (row) => row.backOrderCommittedCount,
    }),
    typeColumn({
      id: 'potf',
      sortKey: 'potf_value',
      value: (row) => row.potfValue,
      count: (row) => row.potfCount,
      committed: (row) => row.potfCommittedCount,
    }),
    typeColumn({
      id: 'stock',
      sortKey: 'stock_value',
      value: (row) => row.stockValue,
      count: (row) => row.stockCount,
      committed: (row) => row.stockCommittedCount,
    }),
    {
      id: 'sharing',
      size: 84,
      sortKey: 'shareable_value',
      cell: (row) => (
        <TypeValue
          value={row.shareableValue}
          title={
            row.shareableCount === null
              ? undefined
              : tooltipLabels.shareable(row.shareableCount)
          }
        />
      ),
    },
    {
      id: 'lines',
      size: 52,
      sortKey: 'lines',
      cell: (row) => <Count value={row.lineCount} />,
    },
    {
      id: 'core',
      size: 64,
      cell: (row) => <CoreCount value={row.coreLineCount} gt={gt} />,
    },
    {
      id: 'value',
      size: 84,
      sortKey: 'value',
      cell: (row) => formatCurrency(row.cartTotal),
    },
    {
      id: 'poMinimum',
      size: 72,
      cell: (row) => <VendorMinimum value={row.movThreshold} />,
    },
    {
      id: 'prepaid',
      size: 72,
      cell: (row) => <VendorMinimum value={row.freightThreshold} />,
    },
    {
      id: 'readiness',
      size: 56,
      centerHeader: true,
      cell: (row) => <ReadinessCell row={row} />,
    },
    {
      id: 'queued',
      size: 64,
      cell: (row) =>
        row.kind === 'to' ? <Count value={row.queuedLineCount} /> : null,
    },
    {
      id: 'inboundMode',
      size: 72,
      cell: (row) =>
        row.inboundMode === null ? null : (
          <span className="whitespace-nowrap text-muted-foreground">
            {row.inboundMode === 'auto_stage'
              ? gt('Auto', {
                  context:
                    'buying orders inbound cell: the destination stages transfers automatically',
                })
              : gt('Review', {
                  context:
                    'buying orders inbound cell: the destination reviews each transfer',
                })}
          </span>
        ),
    },
    {
      id: 'oldestSo',
      size: 84,
      sortKey: 'oldest_so',
      cell: (row) => {
        const label = oldestSoLabel(row.oldestSalesOrderDate);
        return label === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          label
        );
      },
    },
  ];
}

/**
 * The Orders columns as the saved view draws them: in its order, minus the
 * ones it hides or a filter narrowed to one value, with its header titles and
 * alignment.
 */
function orderTableColumns({
  specs,
  entries,
  columnVisibility,
  sortHeader,
}: {
  specs: readonly OrderColumnSpec[];
  entries: readonly ColumnConfigEntry[];
  columnVisibility: VisibilityState;
  sortHeader: SortHeaderState;
}): ColumnDef<BuyingOrderRow>[] {
  const byId = new Map(specs.map((spec) => [spec.id, spec]));
  return entries.flatMap((entry) => {
    const spec = byId.get(entry.id);
    if (!spec || entry.hidden || columnVisibility[entry.id] === false) {
      return [];
    }
    const align = entry.alignRight ? 'right' : 'left';
    const { sortKey } = spec;
    return [
      {
        id: entry.id,
        size: spec.size,
        meta: { align },
        header: () => {
          if (sortKey) {
            return (
              <SortHeader
                label={entry.header}
                sortKey={sortKey}
                align={align}
                {...sortHeader}
              />
            );
          }
          return spec.centerHeader ? (
            <span className="block text-center">{entry.header}</span>
          ) : (
            entry.header
          );
        },
        cell: ({ row }) => (
          <ColumnStyledCell style={entry.style}>
            {spec.cell(row.original)}
          </ColumnStyledCell>
        ),
      },
    ];
  });
}

/** Previous / next pages, shown once the orders run past one page. */
function OrdersPager({
  totalRows,
  page,
  onPageChange,
}: {
  totalRows: number;
  page: number;
  onPageChange: (page: number) => void;
}) {
  const gt = useGT();
  if (totalRows <= BUYING_ORDERS_PAGE_SIZE) return null;
  const pageCount = Math.max(1, Math.ceil(totalRows / BUYING_ORDERS_PAGE_SIZE));
  return (
    <div className="flex items-center justify-end gap-2 px-2 pb-1 text-xs text-muted-foreground">
      <span className="tabular-nums">
        {gt('Page {page} of {pages}', {
          context: 'buying orders pager',
          page: String(page),
          pages: String(pageCount),
        })}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="h-7 w-7"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label={gt('Previous page', { context: 'buying orders pager' })}
      >
        <CaretLeftIcon className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="h-7 w-7"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        aria-label={gt('Next page', { context: 'buying orders pager' })}
      >
        <CaretRightIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/** DOM id of a master row, the target of the table's aria-activedescendant. */
const buyingRowDomId = (rowKey: string) => `buying-row-${rowKey}`;

export function BuyingOrdersTable({
  rows,
  totalRows,
  isLoading,
  sort,
  direction,
  onSortChange,
  isSelected,
  cursorKey = null,
  onSelect,
  page,
  onPageChange,
  columnVisibility = ALL_COLUMNS_VISIBLE,
  columnConfig,
}: BuyingOrdersTableProps) {
  const gt = useGT();

  // The React Compiler memoizes these on `gt` and the view.
  const baseColumns = orderColumnSpecs(gt);

  const columns = orderTableColumns({
    specs: baseColumns,
    entries: columnConfig.columns,
    columnVisibility,
    sortHeader: { sort, direction, onSortChange },
  });
  const controlled = useBuyerBoardTableState(
    columnConfig,
    columns.flatMap((column) => column.id ?? []),
  );

  const table = useTable({
    features: appTableFeatures,
    columns,
    data: rows,
    getRowId: buyingRowKey,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    onColumnSizingChange: controlled.onColumnSizingChange,
    state: {
      columnOrder: controlled.columnOrder,
      columnSizing: controlled.columnSizing,
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-1 p-1" aria-busy="true">
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} className="h-7 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <DataTable
        table={table}
        density="compact"
        // Flush in the Orders section, whose frame already draws the edges.
        className="rounded-none border-0"
        onRowClick={(row) => onSelect(row)}
        tableProps={{
          role: 'grid',
          tabIndex: 0,
          'aria-label': gt('Open orders', {
            context: 'buying orders table accessible name',
          }),
          'aria-activedescendant':
            cursorKey === null ? undefined : buyingRowDomId(cursorKey),
          className:
            'outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        }}
        getRowDomId={(row) => buyingRowDomId(row.id)}
        getRowClassName={(row) =>
          cn(
            isSelected(row.original) && SELECTED_ROW_CLASS,
            // The cursor is an outline, so it still reads on the opened row.
            cursorKey !== null &&
              row.id === cursorKey &&
              'bg-muted/50 outline-2 -outline-offset-2 outline-ring',
          )
        }
        getRowAriaSelected={(row) => isSelected(row.original)}
        emptyState={
          <span className="text-muted-foreground">
            {gt('No open orders match these filters', {
              context: 'buying orders table empty state',
            })}
          </span>
        }
      />
      <OrdersPager
        totalRows={totalRows}
        page={page}
        onPageChange={onPageChange}
      />
    </div>
  );
}
